import { useEffect, useRef } from 'react'
import { useLocation, useNavigate, useRouteError } from 'react-router-dom'
import { describeRouteError } from '@/components/shell/route-error.ts'
import { Button } from '@/components/ui/primitives.tsx'
import { useT } from '@/i18n/index.ts'

/**
 * What a page shows when it throws while rendering, in place of React Router's developer screen
 * ("Unexpected Application Error!", no way out but the address bar).
 *
 * The usual cause is version skew (PRD-26 D-66). For a minute during a rollout, or after one half
 * is rolled back, the app and the API are a release apart, and a page that reads a field the
 * other release does not send throws. Reloading fetches the app that matches the API, so Reload is
 * the first action.
 *
 * Inside the shell by default, so the navigation keeps working and the failure is one page's, not
 * the whole app's. `fullPage` is for a failure in the shell itself, or on the sign-in page.
 */
export const RouteError = ({ fullPage = false }: { fullPage?: boolean }) => {
  const error = useRouteError()
  const navigate = useNavigate()
  const onHome = useLocation().pathname === '/'
  const heading = useRef<HTMLHeadingElement>(null)
  // In the body, never captured at module scope: this screen outlives a locale switch, and a
  // frozen capture would keep apologising in the previous language (PRD-27 F-3).
  const messages = useT()
  const t = messages.shell.routeError

  // The page the person was looking at is gone; say so where their focus and their screen reader
  // are, rather than leaving focus on whatever the failed page had removed.
  useEffect(() => heading.current?.focus(), [])

  const message = (
    <div className="mx-auto flex max-w-md flex-col items-center gap-3 px-4 py-16 text-center">
      {/* Focused so keyboard and screen-reader users start here. It keeps the app's ring, like
          every heading focus rescue lands on: Chrome shows it only when focus came by keyboard. */}
      <h1 ref={heading} tabIndex={-1} className="display text-[28px] sm:text-[32px]">
        {t.title}
      </h1>
      <p className="text-pretty text-[13.5px] leading-relaxed text-fg-muted">{t.body}</p>
      <div className="mt-1 flex flex-wrap items-center justify-center gap-2">
        <Button variant="primary" onClick={() => window.location.reload()}>
          {t.reload}
        </Button>
        {/* On the home screen already, "go home" would land on this same failure. */}
        {onHome ? null : (
          <Button variant="ghost" onClick={() => navigate('/')}>
            {t.home}
          </Button>
        )}
      </div>
      <details className="mt-3 w-full text-[12px] text-fg-subtle">
        <summary className="cursor-pointer select-none">{t.details}</summary>
        <p className="mt-2 break-words font-mono text-[11px] leading-relaxed">{describeRouteError(messages, error)}</p>
      </details>
    </div>
  )

  return fullPage ? (
    <div className="flex min-h-dvh items-center justify-center bg-desk text-fg">{message}</div>
  ) : (
    message
  )
}
